import { useEffect, useMemo, useState } from 'react'
import placeholderImage from 'assets/images/placeholder.png'
import { useRouter } from 'next/router'
import { CheckCircleIcon } from 'components/icons'
import Button from 'components/Button'
import Image from 'components/Image'
import { useQuery } from 'urql'
import { config } from 'data/config'
import { joinBy } from 'data/helpers/strings'
import ServiceRequestModalWithAuth from 'components/modals/ServiceRequestModalWithAuth'
import MagazineSection from 'components/sections/MagazineSection'
import InviteFriendSection from 'components/sections/InviteFriendSection'
import { graphql } from 'gql'
import PublicLayout, { PublicLayoutProps } from 'layouts/PublicLayout'
import PageHeader from 'components/sections/page/PageHeader'
import { useScreenWidth } from 'data/hooks/useScreenWidth'
import Headers from 'components/home/common/typography/Headers'
import CarouselImages from 'components/carousels/CarouselImages'
import MostPopularServicesSection from 'components/sections/MostPopularServicesSection'
import { useHasScrolled } from 'data/hooks/useHasScrolled'
import { useMobileApp } from 'data/hooks/MobileApp/useMobileApp'
import Pill from 'components/home/Pill'

const SERVICE_PAGE_QUERY = graphql(`
  query Service($slug: String) {
    service(slug: $slug) {
      id
      title
      slug
      survey_slug
      subtitle
      description
      page_description
      service_details
      category {
        id
        title
      }
      sub_category {
        id
        title
      }
      form_link
      is_featured
      service_image {
        id
        url
      }
      image_alt_text
      images {
        id
        url
        file_name
      }
      seo_title
      seo_description
    }
  }
`)

const ServicePage = () => {
  const router = useRouter()

  const hasScrolled = useHasScrolled(0, true)
  const { isMobileApp } = useMobileApp()

  const [{ data }] = useQuery({
    query: SERVICE_PAGE_QUERY,
    variables: {
      slug: router?.query?.service as string,
    },
  })

  const [isServiceRequestModalOpen, setIsServiceRequestModalOpen] = useState(false)

  useEffect(() => {
    if (service === null && typeof window !== 'undefined') {
      router.replace('/services/all')
    }
  }, [data?.service])

  const service = useMemo(() => data?.service, [data?.service])

  const { isMobile } = useScreenWidth(1024)

  const seo: PublicLayoutProps['seo'] = {
    title: service?.seo_title ?? service?.title,
    description: service?.seo_description ?? undefined,
    openGraph: {
      type: 'website',
      url: `${config.appURL}/services/${service?.slug}`,
      images: service?.service_image?.url
        ? [
          {
            url: service?.service_image?.url,
            width: 922,
            height: 700,
          },
        ]
        : undefined,
    },
  }

  const mobileNavBar: PublicLayoutProps['mobileNavBar'] = {
    visible: hasScrolled,
    fade: isMobileApp ?? false,
    back: true,
    profileTrigger: true,
    images: [
      {
        url: service?.service_image?.url ?? '',
        alt_text: service?.image_alt_text ?? '',
      },
      ...(service?.images
        .filter(image => image.url)
        .map(image => ({
          url: image.url!,
          alt_text: image.file_name,
        })) ?? []),
    ],
  }

  const actionFooter: PublicLayoutProps['actionFooter'] = {
    button: {
      children: `Book ${service?.category?.title}`,
      onClick: () => setIsServiceRequestModalOpen(true),
    },
  }

  if (!service) {
    return null
  }

  const [serviceDescription, servicePrice] = service.description?.split('\n') ?? [undefined, undefined]

  return (
    <PublicLayout
      seo={seo}
      mobileNavBar={mobileNavBar}
      actionFooter={actionFooter}
    >
      <div className="flex flex-col w-full antialiased lg:flex-col-reverse">

        <div>
          <hr className="hidden text-grey-100 lg:block" />
          <div className="flex flex-col lg:flex-row lg:w-full justify-center max-w-[1800px] mx-auto">
            <div className="hidden lg:block xl:max-w-[992px] w-full lg:h-[700px] relative" >
              <Image
                src={service.service_image?.url ?? placeholderImage}
                alt={service.image_alt_text ?? service.title}
                className="absolute inset-0 object-cover"
                sizes="50vw"
                layout="fill"
              />
            </div>

            {isMobile
              ? (
                <div className="flex flex-col items-center text-center gap-y-10">
                  <PageHeader
                    label={joinBy([service.category?.title, service.sub_category?.title], ' - ')}
                    heading={service.title}
                  />

                  {
                    serviceDescription && (
                      <p className="px-40 mt-10 text-14 leading-22 tracking-1/4 text-grey-900">
                        {serviceDescription}
                      </p>
                    )
                  }

                  {
                    servicePrice && (
                      <Pill
                        variant={'grey-sans'}
                        size={'8'}
                      >
                        {servicePrice}
                      </Pill>
                    )
                  }

                  {
                    service.subtitle && (
                      <h2 className="mt-25 font-serif font-bold px-[28px] text-22 leading-23 -tracking-1/4 text-grey-900">
                        {service.subtitle}
                      </h2>
                    )
                  }
                </div>
              )
              : (
                <div className="flex flex-col justify-center w-full max-w-[650px] px-50 md:px-75 mx-auto rounded-10 md:rounded-none mt-50">
                  <p className="font-bold uppercase text-primary text-opacity-80 text-12 lg:text-14 leading-20 tracking-1/2">
                    {joinBy([service.category?.title, service.sub_category?.title], ' - ')}
                  </p>

                  <h1 className="font-serif font-bold mt-25 text-36 lg:text-48 leading-title tracking-title text-grey-900">
                    {service.title}
                  </h1>

                  <h2 className="font-serif mt-25 text-grey-800 text-18 lg:text-26 leading-title tracking-title">
                    {service.subtitle}
                  </h2>

                  <div className="max-w-[260px] w-full flex flex-col mx-auto items-center md:mt-80">
                    <Button
                      block
                      onClick={() => setIsServiceRequestModalOpen(true)}
                      disabled={!service.form_link}
                    >
                      Request Service
                    </Button>

                    <div className="flex flex-col gap-10 text-center mt-25 text-14 leading-16">
                      {service.description?.split('\n').map(
                        (description, index) => (
                          <p key={index} className={index === 0 ? 'text-grey-900' : 'text-grey-750'}>
                            {description}
                          </p>
                        ))}
                    </div>
                  </div>
                </div>
              )
            }
          </div>

          <hr className="mx-20 md:px-0 mt-30 lg:mt-0 text-grey-100" />

          <div className="flex flex-col items-center container-public mt-30 md:mt-75 md:mb-50 gap-y-50 md:gap-y-75">
            {service.page_description && (
              <div className="max-w-[900px] w-full">
                <Headers.XL className="font-serif text-center max-w-[320px] sm:max-w-full mx-auto">
                  Description
                </Headers.XL>

                <div className="text-justify mt-25 md:mt-[56px] text-grey-750 leading-23 text-14 md:text-15" dangerouslySetInnerHTML={{ __html: service.page_description }}></div>
              </div>
            )}

            {(service.images ?? []).length > 0 && (
              <div className="hidden w-screen lg:block">
                {service && (service.images ?? [].length > 0) && (
                  <CarouselImages
                    images={[
                      {
                        url: service.service_image?.url ?? '',
                        alt_text: service.image_alt_text ?? '',
                      },
                      ...(service.images
                        .filter(image => image.url)
                        .map(image => ({
                          url: image.url!,
                          alt_text: image.file_name,
                        })) ?? []),
                    ]}
                  />
                )}
              </div>
            )}

            {service.service_details && (
              <div className="max-w-[900px] w-full">
                <Headers.XL className="font-serif text-center max-w-[320px] sm:max-w-full mx-auto">
                  Service Details
                </Headers.XL>

                <div className="flex flex-col mt-24 gap-y-8 text-grey-750 leading-23 text-14 lg:text-15" >
                  {service.service_details?.split('#')?.map((item, index) => (
                    <p
                      className="flex items-center gap-x-15"
                      key={index}
                    >
                      <CheckCircleIcon className="flex-shrink-0 text-primary text-16" />
                      <span>{item}</span>
                    </p>
                  ))}
                </div>
              </div>
            )
            }
          </div>
        </div>
      </div>

      <hr className="mx-20 my-50 text-grey-100" />

      <MostPopularServicesSection className={{
        container: 'my-50 lg:my-100',
      }}/>

      <InviteFriendSection />

      <MagazineSection />

      <ServiceRequestModalWithAuth
        isOpen={isServiceRequestModalOpen}
        onClose={() => setIsServiceRequestModalOpen(false)}
        service={service}
      />
    </PublicLayout>
  )
}

export default ServicePage
